import React, { useState, useEffect, useCallback } from "react";
import { disciplinedScanAPI } from "../services/api";
import { toast } from "sonner";

/**
 * DisciplinedBot — Bot de trading discipliné (Helmi spec 2026-05-29)
 * - 2 sous-onglets : Top Movers / Watchlist Trading
 * - À la demande uniquement (boutons Analyser/Refresh)
 * - Toggle A+/A only vs Tous
 * - Pas d'auto-exécution, juste analyse + alertes
 */

const BADGE_STYLES = {
  "Clean breakout": "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  "Retest setup": "bg-cyan-500/20 text-cyan-300 border-cyan-500/40",
  "Extended": "bg-orange-500/20 text-orange-300 border-orange-500/40",
  "Broken momentum": "bg-red-500/20 text-red-300 border-red-500/40",
  "No catalyst": "bg-zinc-500/20 text-zinc-300 border-zinc-500/40",
  "High risk small cap": "bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/40",
  "Low liquidity": "bg-amber-500/20 text-amber-300 border-amber-500/40",
  "Bad R/R": "bg-rose-500/20 text-rose-300 border-rose-500/40",
  "Missing data": "bg-slate-500/20 text-slate-300 border-slate-500/40",
};

const DECISION_STYLES = {
  BUY: "bg-emerald-500/15 text-emerald-300 border-emerald-500/50 shadow-emerald-500/20",
  WAIT: "bg-amber-500/15 text-amber-300 border-amber-500/50 shadow-amber-500/20",
  "NO TRADE": "bg-zinc-700/40 text-zinc-400 border-zinc-600/50",
  EXIT: "bg-rose-500/20 text-rose-300 border-rose-500/50",
  INVALIDATED: "bg-red-600/20 text-red-300 border-red-600/50",
};

const STATUS_STYLES = {
  "A+": "bg-gradient-to-r from-emerald-500 to-teal-500 text-white",
  "A": "bg-emerald-600/80 text-white",
  "B": "bg-amber-600/80 text-white",
  "NO TRADE": "bg-zinc-700 text-zinc-300",
};

function fmtPrice(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  return `$${Number(v).toFixed(2)}`;
}

function Badge({ label }) {
  const cls = BADGE_STYLES[label] || "bg-slate-700/30 text-slate-300 border-slate-600/40";
  return (
    <span
      data-testid={`disc-badge-${label.replace(/\s+/g, "-").toLowerCase()}`}
      className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${cls}`}
    >
      {label}
    </span>
  );
}

function ResultCard({ result }) {
  const [expanded, setExpanded] = useState(false);
  const decision = (result.decision || "NO TRADE").toUpperCase();
  const status = result.setup_status || "NO TRADE";
  const plan = result.plan || {};
  const levels = result.levels || {};

  return (
    <div
      data-testid={`disc-card-${result.ticker}`}
      className="bg-zinc-900/80 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-all"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xl font-bold text-white">{result.ticker}</span>
            <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_STYLES[status] || STATUS_STYLES["NO TRADE"]}`}>
              {status}
            </span>
            <span className="text-zinc-500 text-xs">· score {result.score}/100</span>
          </div>
          <p className="text-xs text-zinc-400 mt-1 truncate">{result.company_name}</p>
        </div>
        <div
          data-testid={`disc-decision-${result.ticker}`}
          className={`shrink-0 text-xs font-bold px-3 py-1.5 rounded border ${DECISION_STYLES[decision] || DECISION_STYLES["NO TRADE"]}`}
        >
          {decision}
        </div>
      </div>

      {/* Badges */}
      {result.badges && result.badges.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {result.badges.map((b) => <Badge key={b} label={b} />)}
        </div>
      )}

      {/* Key metrics row */}
      <div className="grid grid-cols-3 gap-3 mb-3 text-xs">
        <div>
          <p className="text-zinc-500 uppercase tracking-wider">Prix</p>
          <p className="text-white font-semibold">{fmtPrice(result.snapshot?.price)}</p>
        </div>
        <div>
          <p className="text-zinc-500 uppercase tracking-wider">Var. jour</p>
          <p className={`font-semibold ${(result.snapshot?.day_change_pct ?? 0) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
            {result.snapshot?.day_change_pct ? `${result.snapshot.day_change_pct > 0 ? "+" : ""}${result.snapshot.day_change_pct}%` : "—"}
          </p>
        </div>
        <div>
          <p className="text-zinc-500 uppercase tracking-wider">Vol. relatif</p>
          <p className="text-white font-semibold">{result.snapshot?.rel_volume ? `${result.snapshot.rel_volume}x` : "—"}</p>
        </div>
      </div>

      {/* Plan */}
      <div className="bg-zinc-950/60 border border-zinc-800/60 rounded-lg p-3 mb-2">
        <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Plan de trade</p>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          <div className="flex justify-between"><span className="text-zinc-400">Entrée</span><span className="text-white font-mono">{fmtPrice(plan.entry_ideal)}</span></div>
          <div className="flex justify-between"><span className="text-zinc-400">Stop</span><span className="text-rose-300 font-mono">{fmtPrice(plan.stop_loss)}</span></div>
          <div className="flex justify-between"><span className="text-zinc-400">Cible 1</span><span className="text-emerald-300 font-mono">{fmtPrice(plan.target_1)}</span></div>
          <div className="flex justify-between"><span className="text-zinc-400">Cible 2</span><span className="text-emerald-300 font-mono">{fmtPrice(plan.target_2)}</span></div>
          <div className="flex justify-between"><span className="text-zinc-400">R/R</span><span className="text-cyan-300 font-mono">{plan.rr ? `${plan.rr.toFixed(2)}:1` : "—"}</span></div>
          <div className="flex justify-between"><span className="text-zinc-400">Risque</span><span className="text-orange-300 font-mono">{fmtPrice(plan.risk_per_share)}</span></div>
        </div>
      </div>

      {/* Final instruction */}
      {result.final_instruction && (
        <div className="text-xs text-zinc-300 italic border-l-2 border-zinc-700 pl-2 mb-2">
          {result.final_instruction}
        </div>
      )}

      {/* Discipline overrides flag */}
      {result.discipline_overrides && result.discipline_overrides.length > 0 && (
        <div className="text-[10px] text-amber-400 mb-1">
          ⚠ Downgrade auto : {result.discipline_overrides.join(", ")}
        </div>
      )}

      {/* Expandable details */}
      <button
        data-testid={`disc-expand-${result.ticker}`}
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full text-[11px] text-zinc-500 hover:text-zinc-300 mt-1"
      >
        {expanded ? "▲ Masquer détails" : "▼ Voir détails (niveaux, catalyseur, analyse)"}
      </button>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-zinc-800 space-y-3 text-xs">
          {/* Niveaux */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Niveaux clés</p>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1">
              <div className="flex justify-between"><span className="text-zinc-400">Sup. principal</span><span className="text-white font-mono">{fmtPrice(levels.support_main ?? result.snapshot?.support_main)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Sup. court</span><span className="text-white font-mono">{fmtPrice(levels.support_short ?? result.snapshot?.support_short)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Rés. immédiate</span><span className="text-white font-mono">{fmtPrice(levels.resistance_immediate ?? result.snapshot?.resistance_immediate)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Rés. suivante</span><span className="text-white font-mono">{fmtPrice(levels.resistance_next ?? result.snapshot?.resistance_next)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Pre-mkt high</span><span className="text-white font-mono">{fmtPrice(levels.premarket_high ?? result.snapshot?.premarket_high)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Pre-mkt low</span><span className="text-white font-mono">{fmtPrice(levels.premarket_low ?? result.snapshot?.premarket_low)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">VWAP</span><span className="text-white font-mono">{fmtPrice(levels.vwap ?? result.snapshot?.vwap)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Invalidation</span><span className="text-rose-300 font-mono text-[11px]">{plan.invalidation || "—"}</span></div>
            </div>
          </div>

          {/* Catalyseur */}
          {result.catalyst && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Catalyseur</p>
              <p className="text-zinc-300">{result.catalyst}</p>
            </div>
          )}

          {/* Analyse */}
          {result.analysis && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Analyse</p>
              <p className="text-zinc-300 whitespace-pre-wrap">{result.analysis}</p>
            </div>
          )}

          {/* Données manquantes */}
          {result.missing_data && result.missing_data !== "none" && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Données manquantes</p>
              <p className="text-amber-400">{result.missing_data}</p>
            </div>
          )}

          <p className="text-[10px] text-zinc-600">
            {new Date(result.timestamp).toLocaleString("fr-FR")} · LLM: {result.llm_model || "gpt-4o"}
          </p>
        </div>
      )}
    </div>
  );
}

export default function DisciplinedBot({ owner = "Helmi" }) {
  const [subtab, setSubtab] = useState("top_movers");
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterAonly, setFilterAonly] = useState(true);
  const [watchlist, setWatchlist] = useState([]);
  const [wlInput, setWlInput] = useState("");
  const [lastAnalyzedAt, setLastAnalyzedAt] = useState(null);

  // Load watchlist on mount
  useEffect(() => {
    disciplinedScanAPI.getWatchlist(owner)
      .then(d => setWatchlist(d.tickers || []))
      .catch(() => setWatchlist([]));
  }, [owner]);

  const runAnalyze = useCallback(async () => {
    setLoading(true);
    try {
      const r = await disciplinedScanAPI.analyze({
        owner,
        scan_type: subtab,
        max_tickers: subtab === "watchlist" ? Math.min(watchlist.length || 8, 10) : 8,
      });
      setResults(r.results || []);
      setSummary(r.summary || null);
      setLastAnalyzedAt(new Date().toISOString());

      // Alerte si setup A+ ou A trouvé
      const aPlus = (r.results || []).filter(x => (x.setup_status || "").toUpperCase() === "A+");
      const aGood = (r.results || []).filter(x => (x.setup_status || "").toUpperCase() === "A");
      if (aPlus.length > 0) {
        toast.success(`🎯 ${aPlus.length} setup A+ détecté : ${aPlus.map(x => x.ticker).join(", ")}`, { duration: 8000 });
      } else if (aGood.length > 0) {
        toast.info(`✅ ${aGood.length} setup A détecté : ${aGood.map(x => x.ticker).join(", ")}`, { duration: 6000 });
      } else {
        toast.info(`Aucun A+/A détecté — ${r.summary?.no_trade || 0} NO TRADE`);
      }
    } catch (e) {
      console.error(e);
      toast.error("Erreur analyse — voir console");
    } finally {
      setLoading(false);
    }
  }, [owner, subtab, watchlist]);

  const saveWatchlist = useCallback(async () => {
    const tickers = wlInput
      .split(/[,\s]+/)
      .map(t => t.trim().toUpperCase())
      .filter(t => t && /^[A-Z][A-Z0-9\.\-]{0,9}$/.test(t));
    if (tickers.length === 0) {
      toast.error("Aucun ticker valide trouvé");
      return;
    }
    try {
      const r = await disciplinedScanAPI.setWatchlist(owner, tickers);
      setWatchlist(r.tickers || []);
      setWlInput("");
      toast.success(`Watchlist mise à jour : ${r.count} tickers`);
    } catch (e) {
      toast.error("Erreur sauvegarde watchlist");
    }
  }, [wlInput, owner]);

  const filtered = filterAonly
    ? results.filter(r => ["A+", "A"].includes((r.setup_status || "").toUpperCase()))
    : results;

  return (
    <div data-testid="disciplined-bot-tab" className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-white">🤖 Bot Discipliné</h2>
          <p className="text-xs text-zinc-400 mt-1">Scoring strict A+/A/B/NO TRADE · Stop obligatoire · R/R minimum 2:1</p>
        </div>
        <button
          data-testid="disc-analyze-btn"
          type="button"
          onClick={runAnalyze}
          disabled={loading || (subtab === "watchlist" && watchlist.length === 0)}
          className="px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {loading ? "⏳ Analyse en cours..." : (results.length ? "🔄 Refresh" : "▶ Analyser")}
        </button>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-zinc-800">
        <button
          data-testid="disc-subtab-movers"
          type="button"
          onClick={() => setSubtab("top_movers")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${subtab === "top_movers" ? "border-emerald-500 text-emerald-300" : "border-transparent text-zinc-400 hover:text-zinc-200"}`}
        >
          📈 Top Movers du jour
        </button>
        <button
          data-testid="disc-subtab-watchlist"
          type="button"
          onClick={() => setSubtab("watchlist")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${subtab === "watchlist" ? "border-cyan-500 text-cyan-300" : "border-transparent text-zinc-400 hover:text-zinc-200"}`}
        >
          ⭐ Watchlist Trading ({watchlist.length})
        </button>
      </div>

      {/* Watchlist editor (only on watchlist subtab) */}
      {subtab === "watchlist" && (
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3 space-y-2">
          <p className="text-xs text-zinc-400">Watchlist actuelle : <span className="text-cyan-300">{watchlist.length > 0 ? watchlist.join(", ") : "vide"}</span></p>
          <div className="flex gap-2">
            <input
              data-testid="disc-wl-input"
              type="text"
              value={wlInput}
              onChange={(e) => setWlInput(e.target.value)}
              placeholder="Ex: AAPL, DELL, VRT, NVDA, MSFT"
              className="flex-1 bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-cyan-500 focus:outline-none"
            />
            <button
              data-testid="disc-wl-save"
              type="button"
              onClick={saveWatchlist}
              className="px-3 py-2 rounded bg-cyan-700 hover:bg-cyan-600 text-white text-xs font-semibold"
            >
              Enregistrer
            </button>
          </div>
        </div>
      )}

      {/* Summary + filter */}
      {summary && (
        <div className="flex flex-wrap items-center justify-between gap-3 bg-zinc-900/40 border border-zinc-800 rounded-lg px-3 py-2">
          <div className="flex flex-wrap gap-3 text-xs">
            <span className="text-zinc-500">{results.length} analysé(s)</span>
            <span className="text-emerald-400">BUY : {summary.buy}</span>
            <span className="text-amber-400">WAIT : {summary.wait}</span>
            <span className="text-zinc-400">NO TRADE : {summary.no_trade}</span>
            <span className="text-rose-400">EXIT : {summary.exit}</span>
            <span className="text-cyan-300">A+/A : {summary.a_plus_or_a}</span>
          </div>
          <label data-testid="disc-filter-toggle" className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
            <input
              type="checkbox"
              checked={filterAonly}
              onChange={(e) => setFilterAonly(e.target.checked)}
              className="accent-emerald-500"
            />
            A+/A seulement
          </label>
        </div>
      )}

      {/* Last run timestamp */}
      {lastAnalyzedAt && (
        <p className="text-[11px] text-zinc-600">
          Dernière analyse : {new Date(lastAnalyzedAt).toLocaleString("fr-FR")} · GPT-4o
        </p>
      )}

      {/* Results grid */}
      {loading ? (
        <div data-testid="disc-loading" className="text-center py-10">
          <div className="inline-block animate-spin h-10 w-10 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
          <p className="text-zinc-400 text-sm mt-3">Analyse disciplinée en cours (GPT-4o)…</p>
          <p className="text-zinc-600 text-[11px] mt-1">~10-30 secondes selon le nombre de tickers</p>
        </div>
      ) : filtered.length === 0 ? (
        <div data-testid="disc-empty-state" className="bg-zinc-900/40 border border-zinc-800 border-dashed rounded-xl p-8 text-center">
          {results.length === 0 ? (
            <>
              <p className="text-zinc-300 font-medium">Pas encore d'analyse</p>
              <p className="text-xs text-zinc-500 mt-1">
                {subtab === "watchlist" && watchlist.length === 0
                  ? "Ajoute des tickers à ta watchlist puis clique Analyser."
                  : "Clique sur Analyser pour scanner les top movers du jour."}
              </p>
            </>
          ) : (
            <>
              <p className="text-zinc-300">Aucun setup A+/A détecté</p>
              <p className="text-xs text-zinc-500 mt-1">Décoche le filtre pour voir tous les résultats (incluant NO TRADE).</p>
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((r) => <ResultCard key={`${r.ticker}-${r.timestamp}`} result={r} />)}
        </div>
      )}
    </div>
  );
}
